import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { applyUpdateIfSafe, checkForUpdate, isUpdatePending, sectionOf } from '../utils/appUpdate';
import { installUpdateMomentTracking, notePageChange, RETURN_AFTER_HIDDEN_MS } from '../utils/updateMoment';

/**
 * A dialog that closes as the user leaves (a search result picked from the
 * header's search, say) is still on screen for its exit transition when the
 * new section first renders. Look again once, this long after the move.
 */
export const NAVIGATION_RECHECK_MS = 500;

/**
 * Loads a waiting new build at a safe moment (see utils/appUpdate.ts and
 * utils/updateMoment.ts). Renders nothing. Must be inside the Router.
 *
 * - When the user moves to a different section. The reload lands on the page
 *   they were going to: the address has already changed.
 * - When they come back to a tab that was hidden for RETURN_AFTER_HIDDEN_MS or
 *   more, if the page they left has nothing in progress. Coming back also asks
 *   the server for a newer build straight away, so it is ready for the next
 *   section change.
 *
 * At a moment that is not safe nothing happens; the next one is tried instead.
 */
export default function AppUpdateOnNavigation() {
  const { pathname } = useLocation();
  const section = sectionOf(pathname);
  const prev = useRef(section);
  const current = useRef(pathname);

  useEffect(() => {
    installUpdateMomentTracking();
  }, []);

  // Declared before the section effect, so it runs first on the same render.
  useEffect(() => {
    current.current = pathname;
    notePageChange();
  }, [pathname]);

  useEffect(() => {
    if (prev.current === section) return undefined;
    prev.current = section;
    if (!isUpdatePending() || applyUpdateIfSafe('navigation', current.current)) return undefined;
    const t = setTimeout(() => applyUpdateIfSafe('navigation', current.current), NAVIGATION_RECHECK_MS);
    return () => clearTimeout(t);
  }, [section]);

  useEffect(() => {
    let hiddenAt: number | null = document.visibilityState === 'hidden' ? Date.now() : null;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      const since = hiddenAt;
      hiddenAt = null;
      if (since === null || Date.now() - since < RETURN_AFTER_HIDDEN_MS) return;
      if (!applyUpdateIfSafe('return', current.current)) checkForUpdate();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  return null;
}
